import { auth } from './firebase';
import { BuddyProgressSnapshot, buildProgressSnapshot } from './buddy-progress';

// Client helpers for the Watch Together (shared show progress) feature.
// All reads/writes go through /api/buddy/* so Firestore stays locked down.

export interface BuddyLink {
  id: string;
  showId: number;
  showName: string;
  posterPath: string | null;
  status: 'pending' | 'active';
  createdBy: string;
  participants: string[];
  names: Record<string, string>;
  progress: Record<string, BuddyProgressSnapshot>;
  createdAt: string;
  inviteGroupId: string;
}

async function authedFetch(path: string, init: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
}

export const BuddyService = {
  // One invite link can cover several shows and be accepted by several
  // friends — each accept creates a private pairwise link with the inviter.
  // Optionally delivers the invite in-app to a username.
  async createInvite(
    showIds: number | number[],
    inviteUsername?: string
  ): Promise<{ buddyId: string; link: string; notified: boolean; notifiedName?: string; warning?: string }> {
    const ids = Array.isArray(showIds) ? showIds : [showIds];
    const res = await authedFetch('/api/buddy/create', {
      method: 'POST',
      body: JSON.stringify({ showIds: ids, inviteUsername }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to create invite');
    return {
      buddyId: data.buddyId,
      link: `${window.location.origin}/buddy/${data.buddyId}`,
      notified: !!data.notified,
      notifiedName: data.notifiedName,
      warning: data.error,
    };
  },

  async getInviteInfo(buddyId: string) {
    const res = await fetch(`/api/buddy/accept?id=${encodeURIComponent(buddyId)}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Invite not found');
    return data.invite as {
      inviterName: string;
      status: string;
      shows: Array<{ showId: number; showName: string; posterPath: string | null; backdropPath: string | null }>;
    };
  },

  // showIds optionally limits the accept to a subset of the invite's shows
  async acceptInvite(buddyId: string, showIds?: number[]): Promise<{ joined?: number; skippedHidden?: string[] }> {
    const res = await authedFetch('/api/buddy/accept', {
      method: 'POST',
      body: JSON.stringify({ buddyId, showIds }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to accept invite');
    return data;
  },

  async listBuddies(showId?: number): Promise<BuddyLink[]> {
    const res = await authedFetch(`/api/buddy/list${showId ? `?showId=${showId}` : ''}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to load');
    return data.buddies;
  },

  async leave(buddyId: string): Promise<void> {
    const res = await authedFetch('/api/buddy/leave', {
      method: 'POST',
      body: JSON.stringify({ buddyId }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to leave');
  },

  // Delete an entire open invite (every pending show it covers)
  async revokeInvite(groupId: string): Promise<void> {
    const res = await authedFetch('/api/buddy/leave', {
      method: 'POST',
      body: JSON.stringify({ buddyId: groupId, revokeGroup: true }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to revoke invite');
  },

  // Fire-and-forget: push current progress to buddy links after marking
  // episodes/seasons. Failures are silent — next mark retries naturally.
  syncProgress(showId: number): void {
    authedFetch('/api/buddy/sync', {
      method: 'POST',
      body: JSON.stringify({ showId }),
    }).catch(() => {});
  },

  // Detect and repair stale self-progress: if a linked show's stored snapshot
  // doesn't match the local watchlist (e.g. the show was added or watched
  // AFTER accepting the invite, so sharedProgress never got set), push a
  // fresh sync and restore the flag so future episode marks sync too.
  reconcileMyProgress(
    buddies: BuddyLink[],
    myUid: string,
    watchlist: any[],
    updateWatchlistItem: (id: number, updates: any) => void
  ): void {
    const seen = new Set<number>();
    for (const buddy of buddies) {
      if (buddy.status !== 'active' || seen.has(buddy.showId)) continue;
      seen.add(buddy.showId);

      const localItem = watchlist.find(i => i.id === buddy.showId && i.type === 'tv');
      if (!localItem) continue;

      if (!localItem.sharedProgress) {
        updateWatchlistItem(buddy.showId, { sharedProgress: true });
      }

      const local = buildProgressSnapshot(localItem);
      const remote = buddy.progress?.[myUid];
      const stale =
        !remote ||
        remote.episodesWatched !== local.episodesWatched ||
        (remote.lastEpisode || null) !== (local.lastEpisode || null);
      if (stale) {
        this.syncProgress(buddy.showId);
      }
    }
  },
};
